import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ComboBox,
  Grid,
  Modal,
  ModalContent,
  SelectInput,
  TextArea,
  TextInput,
  Typography,
  useToaster
} from "@dealer-center/core-components";
import { SaveAndCancel } from "@dealer-center/core-custom-components";
import {
  runNrtRecordTestCheck,
  useCreateNrtRecord,
  useUpdateNrtRecord
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-fetch";
import type {
  CreateNrtRecordRequest,
  NrtClassification,
  NrtRecord,
  NrtRecordFormValidationErrors,
  NrtStatus,
  NrtVehicleType,
  UpdateNrtRecordRequest
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import {
  buildInitialNrtRecordFormState,
  buildInitialNrtTestResult,
  validateNrtRecordForm
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";
import { parseNrtDate, toUnixTimestampString } from "./nrtDate";
import { SR_ONLY_SX } from "./srOnlySx";
import { STATUS_LABEL } from "./StatusChip";
import { TestResultPanel } from "./TestResultPanel";

export interface RecordModalProps {
  mode: "add" | "edit";
  initialValues?: NrtRecord;
  onClose: () => void;
}

const NOTES_ROWS = 4;

const UNSAVED_HINT_SX = {
  color: "text.secondary",
  fontStyle: "italic",
  textAlign: "right"
} as const;

// type="date" only accepts YYYY-MM-DD; stored dates can be epoch-second.
const toDateInputValue = (value: string | null): string => {
  const date = parseNrtDate(value);
  return date ? date.toISOString().slice(0, 10) : (value ?? "");
};

// Record stores classification as one quoted, comma-separated string.
const CLASSIFICATION_SEPARATOR = ",";

const parseClassifications = (value: string | null): string[] =>
  (value ?? "")
    .split(CLASSIFICATION_SEPARATOR)
    .map(token => token.trim().replace(/^["']|["']$/g, ""))
    .filter(Boolean);

const toClassificationOptions = (value: string | null) => {
  const selected = parseClassifications(value);
  return CLASSIFICATION_OPTIONS.filter(option =>
    selected.includes(option.value)
  );
};

const serializeClassifications = (
  options: readonly { value: string }[]
): string | null =>
  options.map(option => `'${option.value}'`).join(CLASSIFICATION_SEPARATOR) ||
  null;

const STATUS_OPTIONS: { label: string; value: NrtStatus }[] = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
  { label: "Testing", value: "TESTING" }
];

const CLASSIFICATION_OPTIONS: {
  label: string;
  value: Exclude<NrtClassification, null>;
}[] = [
  { label: "All", value: "ALL" },
  { label: "Primary", value: "PRIMARY" },
  { label: "Fleet", value: "FLEET" },
  { label: "Wholesale", value: "WHOLESALE" },
  { label: "Exotic", value: "EXOTIC" },
  { label: "Classifieds", value: "CLASSIFIEDS" }
];

const TYPE_OPTIONS: { label: string; value: Exclude<NrtVehicleType, null> }[] =
  [
    { label: "All", value: "ALL" },
    { label: "New", value: "NEW" },
    { label: "Used", value: "USED" }
  ];

export const RecordModal = ({
  mode,
  initialValues,
  onClose
}: RecordModalProps) => {
  const isAddMode = mode === "add";
  const [formState, setFormState] = useState(() => {
    const initialState = buildInitialNrtRecordFormState(initialValues);
    return {
      ...initialState,
      expirationDate: toDateInputValue(initialState.expirationDate)
    };
  });
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [hasBlurredExpirationDate, setHasBlurredExpirationDate] =
    useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [testResult, setTestResult] = useState(buildInitialNrtTestResult);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { toastError, toastSuccess } = useToaster();
  const { mutateAsync: createNrtRecord, isPending: isCreating } =
    useCreateNrtRecord();
  const { mutateAsync: updateNrtRecord, isPending: isUpdating } =
    useUpdateNrtRecord();
  const isSaving = isCreating || isUpdating;

  const validationError = validateNrtRecordForm(formState);

  const classificationComboBoxValue = useMemo(
    () => toClassificationOptions(formState.classification),
    [formState.classification]
  );

  // Baseline to diff against for "dirty" — starts as the opened record, and
  // moves forward to the just-saved values on a successful Save, so the
  // Unsaved-changes hint clears instead of staying stuck on afterward. Every
  // field is a flat string, so a per-key `!==` scan is equivalent to (and
  // much cheaper on every keystroke than) comparing two JSON.stringify
  // snapshots.
  const [lastSavedFormState, setLastSavedFormState] = useState(formState);
  const isDirty = useMemo(
    () =>
      (Object.keys(formState) as (keyof typeof formState)[]).some(
        key => formState[key] !== lastSavedFormState[key]
      ),
    [formState, lastSavedFormState]
  );

  // Aborts the in-flight run when the record closes, so a poll loop cannot
  // outlive the screen that started it.
  const testAbortRef = useRef<AbortController | null>(null);

  useEffect(() => () => testAbortRef.current?.abort(), []);

  // Nothing to test in Add mode; otherwise Test shares Save's validation
  // gate. A dirty form does NOT disable it.
  const testUnavailableReason = isAddMode
    ? "Save this record before testing. Test runs the rule saved for this account and provider."
    : validationError
      ? hasAttemptedSubmit
        ? "Fix the highlighted fields before testing."
        : "This record has invalid data. Save with valid values before testing."
      : null;
  const isTestDisabled = !!testUnavailableReason;

  // Locks every field during either action; close stays unlocked (onHide).
  const isProcessing = testResult.status === "RUNNING" || isSaving;

  // Announces every test outcome via the polite region below (toaster has no
  // aria-live), and additionally raises a page-level toast for the two
  // settled outcomes. One effect, so the two surfaces can't drift apart on a
  // future status change.
  useEffect(() => {
    if (testResult.status === "IDLE") return;

    const label = STATUS_LABEL[testResult.status].toLowerCase();
    setAnnouncement(
      testResult.message
        ? `Test ${label}. ${testResult.message}`
        : `Test ${label}.`
    );

    if (testResult.status === "COMPLETE") {
      toastSuccess(
        "Test complete",
        testResult.message ?? "Test completed successfully."
      );
    } else if (testResult.status === "ERROR") {
      toastError(testResult.message ?? "Test failed.");
    }
  }, [testResult]);

  // Errors stay hidden until the first Save attempt.
  const errorProps = useCallback(
    (field: keyof NrtRecordFormValidationErrors) => {
      const shouldShowError =
        field === "expirationDate"
          ? (hasBlurredExpirationDate || hasAttemptedSubmit) && validationError
          : hasAttemptedSubmit && validationError;
      const message = shouldShowError ? validationError?.[field] : undefined;
      return message ? { errorMessage: message, hasError: true } : {};
    },
    [hasAttemptedSubmit, hasBlurredExpirationDate, validationError]
  );

  const updateField = useCallback(
    <TKey extends keyof typeof formState>(
      field: TKey,
      value: (typeof formState)[TKey]
    ) => {
      // Any field change reverts a shown result to not-yet-tested.
      setTestResult(prev =>
        prev.status === "IDLE" ? prev : buildInitialNrtTestResult()
      );
      // A stale API error no longer describes the values being edited.
      setSubmitError(null);
      if (field === "expirationDate") {
        setHasBlurredExpirationDate(false);
      }
      setFormState(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  // Abandons any in-flight run before closing, so its late result is never
  // applied and polling stops.
  const handleClose = useCallback(() => {
    testAbortRef.current?.abort();
    onClose();
  }, [onClose]);

  // `!initialValues` is a type guard, not a real path — isTestDisabled
  // already covers Add mode, the only case it's undefined.
  const handleRunTest = useCallback(async () => {
    if (isTestDisabled || !initialValues) return;

    const controller = new AbortController();
    testAbortRef.current = controller;

    setTestResult({
      status: "RUNNING",
      timestamp: String(Math.floor(Date.now() / 1000)),
      message: "Running this record's saved rule against live inventory data.",
      items: []
    });

    // D-1: Test always evaluates the SAVED rule, not the current (possibly
    // dirty) form — providerId isn't locked in edit mode like accountId is.
    const result = await runNrtRecordTestCheck(
      {
        accountId: initialValues.accountId,
        providerId: initialValues.providerId
      },
      { signal: controller.signal }
    );

    if (controller.signal.aborted) return;
    // Don't resurrect a stale result if a field edit reset to IDLE, or if a
    // newer run superseded this one.
    setTestResult(prev => (prev.status === "RUNNING" ? result : prev));
  }, [isTestDisabled, initialValues]);

  const handleSave = useCallback(async () => {
    setHasAttemptedSubmit(true);
    if (validationError) return;

    setSubmitError(null);

    // Required fields are non-blank per validationError, but several are
    // typed `string | null` on formState — hence the casts.
    const payload: CreateNrtRecordRequest = {
      accountId: formState.accountId,
      caseNumber: formState.caseNumber,
      classification: formState.classification as string,
      createdBy: formState.createdBy,
      expirationDate: toUnixTimestampString(formState.expirationDate),
      map: formState.map as string,
      notes: formState.notes,
      providerId: formState.providerId,
      select: formState.select as string,
      status: formState.status,
      tags: formState.tags,
      type: formState.type as NrtVehicleType
    };

    try {
      if (isAddMode) {
        await createNrtRecord(payload);
      } else {
        const id = initialValues?.id;
        if (!id) {
          throw new Error("Missing record id — cannot update this record.");
        }
        const { createdBy, ...updateFields } = payload;
        const updatePayload: UpdateNrtRecordRequest = { id, ...updateFields };
        await updateNrtRecord(updatePayload);
      }

      // Create closes the popup — there's nothing more to do with a
      // just-created record here. Update keeps it open (so the just-saved
      // record can be Tested immediately), so it needs the announcement and
      // the re-baselined dirty check instead.
      if (isAddMode) {
        toastSuccess("Success", "Record created successfully!");
        onClose();
      } else {
        const message = "Record updated successfully!";
        toastSuccess("Success", message);
        setAnnouncement(message);
        setLastSavedFormState(formState);
      }
    } catch (error) {
      // API error stays visible in the form and the form stays open.
      setSubmitError(
        error instanceof Error
          ? error.message
          : "An error occurred. Please try again."
      );
    }
  }, [
    validationError,
    isAddMode,
    formState,
    initialValues,
    createNrtRecord,
    updateNrtRecord,
    toastSuccess,
    onClose
  ]);

  return (
    // Paper's own scroll would drag the title along with it, so pin Paper
    // and let DialogContent scroll instead, keeping the title fixed.
    <Modal
      show
      header={isAddMode ? "Add New Record" : "Edit Record"}
      size="fullScreen"
      sx={{
        "& .MuiDialog-paper": { overflowY: "hidden" },
        "& .MuiDialogContent-root": { overflowY: "auto" }
      }}
      // Still dismissible mid-test (abandons the run); blocked only during save.
      onHide={isSaving ? undefined : handleClose}
    >
      <Box aria-live="polite" role="status" sx={SR_ONLY_SX}>
        {announcement}
      </Box>
      <ModalContent>
        {submitError && (
          <Alert role="alert" sx={{ marginBottom: "1.5rem" }} type="error">
            {submitError}
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              {...errorProps("accountId")}
              required
              // Identity field: set once at creation, not editable afterward.
              disabled={isProcessing || !isAddMode}
              id="nrt-record-account-id"
              label="Account ID"
              name="accountId"
              value={formState.accountId}
              onChange={e => updateField("accountId", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              {...errorProps("caseNumber")}
              required
              disabled={isProcessing}
              id="nrt-record-case-number"
              label="Case Number"
              name="caseNumber"
              value={formState.caseNumber}
              onChange={e => updateField("caseNumber", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              {...errorProps("providerId")}
              required
              disabled={isProcessing}
              id="nrt-record-provider-id"
              label="Provider ID"
              name="providerId"
              value={formState.providerId}
              onChange={e => updateField("providerId", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              {...errorProps("createdBy")}
              required
              // Audit field: stamped at creation, not editable afterward.
              disabled={isProcessing || !isAddMode}
              id="nrt-record-created-by"
              label="Created By"
              name="createdBy"
              value={formState.createdBy}
              onChange={e => updateField("createdBy", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <SelectInput
              required
              disabled={isProcessing}
              displayDeselectOption={false}
              id="nrt-record-status"
              label="Status"
              name="status"
              options={STATUS_OPTIONS}
              value={formState.status}
              onChange={e => updateField("status", e.target.value as NrtStatus)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              {...errorProps("expirationDate")}
              required
              disabled={isProcessing}
              id="nrt-record-expiration-date"
              label="Expiration Date"
              name="expirationDate"
              type="date"
              value={formState.expirationDate ?? ""}
              onBlur={() => setHasBlurredExpirationDate(true)}
              onChange={e => updateField("expirationDate", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <ComboBox
              {...errorProps("classification")}
              multiple
              required
              disabled={isProcessing}
              id="nrt-record-classification"
              label="Classification"
              name="classification"
              options={CLASSIFICATION_OPTIONS}
              value={classificationComboBoxValue}
              onChange={e =>
                updateField(
                  "classification",
                  serializeClassifications(
                    e.target.value ?? []
                  ) as NrtClassification
                )
              }
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <SelectInput
              required
              disabled={isProcessing}
              displayDeselectOption={false}
              id="nrt-record-type"
              label="Type"
              name="type"
              options={TYPE_OPTIONS}
              value={formState.type ?? undefined}
              onChange={e =>
                updateField("type", e.target.value as NrtVehicleType)
              }
            />
          </Grid>

          <Grid size={{ md: 4, xs: 12 }}>
            <TextInput
              disabled={isProcessing}
              id="nrt-record-tags"
              label="Tags"
              name="tags"
              value={formState.tags ?? ""}
              onChange={e => updateField("tags", e.target.value)}
            />
          </Grid>

          {/* Rule expressions and notes can share space. */}
          <Grid size={{ md: 6, xs: 12 }}>
            <TextInput
              {...errorProps("select")}
              required
              disabled={isProcessing}
              id="nrt-record-select"
              label="Select"
              name="select"
              placeholder="e.g. misc01 == 'Active'"
              value={formState.select ?? ""}
              onChange={e => updateField("select", e.target.value)}
            />
          </Grid>

          <Grid size={{ md: 6, xs: 12 }}>
            <TextInput
              id="nrt-record-map"
              {...errorProps("map")}
              required
              disabled={isProcessing}
              label="Map"
              name="map"
              placeholder="e.g. wholesalePrice = 799"
              value={formState.map ?? ""}
              onChange={e => updateField("map", e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextArea
              disabled={isProcessing}
              id="nrt-record-notes"
              label="Notes"
              name="notes"
              rows={NOTES_ROWS}
              value={formState.notes ?? ""}
              onChange={e => updateField("notes", e.target.value)}
            />
          </Grid>
        </Grid>
        {testResult.status === "RUNNING" ? (
          // The action row is hidden (not just disabled) while a test runs.
          // Save has its own loading state below instead.
          <Typography sx={{ marginBottom: "1rem" }} variant="body-md">
            Processing…
          </Typography>
        ) : (
          <Box
            sx={{
              alignItems: "flex-end",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "1rem"
            }}
          >
            <Box
              sx={{
                alignItems: "flex-start",
                display: "flex",
                flexDirection: "column",
                gap: "0.25rem"
              }}
            >
              <Button
                analyticsConfiguration={{
                  eventName: "nrt_mapping_tool_run_test"
                }}
                buttonStyle="secondary"
                disabled={isTestDisabled || isSaving}
                onClick={handleRunTest}
              >
                Test
              </Button>
              {testUnavailableReason && (
                <Typography color="text.secondary" variant="body-sm">
                  {testUnavailableReason}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                alignItems: "flex-end",
                display: "flex",
                flexDirection: "column",
                gap: "0.25rem"
              }}
            >
              {isDirty && (
                <Typography
                  data-testid="unsaved-changes-hint"
                  sx={UNSAVED_HINT_SX}
                  variant="body-sm"
                >
                  Unsaved changes
                </Typography>
              )}
              <SaveAndCancel
                analyticsEventName={
                  isAddMode ? "NrtAddRecordForm" : "NrtEditRecordForm"
                }
                isDisabled={{
                  save:
                    !isDirty ||
                    (!!validationError && hasAttemptedSubmit) ||
                    isSaving
                }}
                saveButtonText={
                  isAddMode
                    ? isSaving
                      ? "Creating…"
                      : "Create"
                    : isSaving
                      ? "Saving…"
                      : "Save"
                }
                onCancel={handleClose}
                onSave={handleSave}
              />
            </Box>
          </Box>
        )}
        <TestResultPanel result={testResult} />
      </ModalContent>
    </Modal>
  );
};
